import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuthStore } from '@/store/authStore';
import type { ChatMessage } from '@/services/chatService';

/**
 * Kết nối SignalR hub `/hubs/chat` của backend.
 * Callbacks:
 *   - onMessageReceived: khi có tin mới trong bất kỳ phòng nào user là member
 *   - onRoomUpdated: khi phòng có update (member, name...)
 */
export function useChatHub(options: {
  onMessageReceived?: (msg: ChatMessage) => void;
  onRoomUpdated?: (roomId: string) => void;
}) {
  const { token, isAuthenticated } = useAuthStore();
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const optionsRef = useRef(options);

  // Update ref mỗi khi options thay đổi để handlers luôn dùng phiên bản mới
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api';
    const hubUrl = baseUrl.replace(/\/api\/?$/, '') + '/hubs/chat';

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => token })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('MessageReceived', (msg: ChatMessage) => {
      optionsRef.current.onMessageReceived?.(msg);
    });

    connection.on('RoomUpdated', (roomId: string) => {
      optionsRef.current.onRoomUpdated?.(roomId);
    });

    connection
      .start()
      .then(() => console.log('[ChatHub] Connected'))
      .catch((err) => console.error('[ChatHub] Connection failed:', err));

    connectionRef.current = connection;

    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, [token, isAuthenticated]);

  return connectionRef.current;
}
